import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import Breadcrumbs from 'components/BreadCrumbs';
import {PAGES_URL} from 'helpers/constants';
import ProfileSidebar from './components/ProfileSidebar';
import OnboardingDetails from './components/OnboardingDetails';
import ReactSpeedometer from 'react-d3-speedometer';
import {getLighterShade} from 'routes/EasyOnboard/colorUtils';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {CommentSection} from '../../components/CommentSection';
import {useQuery} from 'react-query';
import {pdf} from '@react-pdf/renderer';
import {fetchAccountDetails} from '../../requests/queries/flows';
import {
  PrimaryButton,
  DashboardLayout,
  SecondaryButton,
  Loader,
} from '../../components';
import moment from 'moment';
import {toast} from 'react-toastify';
import {Constants} from '../../helpers';
import FinancialActivity from './components/FinancialActivity';
import BehavioralAndDevice from './components/BehavioralAndDevice';
import NetworkAndRelationship from './components/NetworkAndRelationship';
import LocationAndGeographic from './components/LocationAndGeographic';
import {getSeverityColor} from './helpers';
import {ProfileReport} from 'routes/FraudDetection/Flows/pdfs/ProfileReport';
import {convertSVGToBase64} from 'helpers/convertSVGToBase64';
import {profiledRiskDark} from 'assets/images/images';
import ActivityLog from '../../components/ActivityLog';

function ProfileRiskDetails() {
  const navigate = useNavigate();
  const [active, setActive] = useState('Customer Information');

  const {userDetails} = useSelector(state => state?.auth);
  const {state} = useLocation();
  const {slug} = useParams();
  const baseColor = userDetails?.company?.color_code || '#3f7cdb';
  const lighterShade = getLighterShade(baseColor, 93);
  const [base64URL, setBase64URL] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    convertSVGToBase64(profiledRiskDark).then(image => setBase64URL(image));
  }, []);

  const isDev = process.env.REACT_APP_APP_ENV_MODE !== 'production';

  const {data: accountDetailsResponse, isLoading} = useQuery(
    ['users-accounts-details', {account_id: slug}],
    () => fetchAccountDetails({account_id: slug}),
    {retry: 1, enabled: !!slug},
  );

  const userEntity = accountDetailsResponse?.entity || {};
  const employerEntity = userEntity?.employer_information || {};
  const identityAndVerification = userEntity?.kyc_information || [];
  const financialData = accountDetailsResponse?.financial_activity || {};
  const behavioralData = accountDetailsResponse?.behavioral_device || {};
  const networkData = accountDetailsResponse?.network_relationship || {};
  const locationData = accountDetailsResponse?.location_geographic || {};

  const averageRiskScore = Math.ceil(
    accountDetailsResponse?.averageRiskScore || 0,
  );

  const quickSummary = {
    riskScoreValue: averageRiskScore > 100 ? 100 : averageRiskScore,
    totalCases: accountDetailsResponse?.totalCases || '0',
    totalEvents:
      accountDetailsResponse?.totalStatuses?.reduce(
        (sum, status) => sum + status.count,
        0,
      ) || 0,
    totalOpenCases: accountDetailsResponse?.totalOpenCases || 0,
    riskTier: userEntity?.tier || '-',
  };

  const onboardingDetails = {
    userEntity,
    employerEntity,
    identityAndVerification,
    financialData,
    behavioralData,
    networkData,
    locationData,
  };

  const reportingEntityName = userDetails?.company?.name || '-';

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await pdf(
        <ProfileReport
          data={onboardingDetails}
          logoImage={base64URL}
          quickSummary={quickSummary}
          reportingEntityName={reportingEntityName}
          userName={userDetails?.user_name || 'System'}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `profile-report-${slug}.pdf`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Profile report downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Could not generate PDF report.');
    }
    setIsDownloading(false);
  };

  const handleCopy = text => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success('Copied successfully');
      })
      .catch(() => {
        toast.error('Failed to copy');
      });
  };

  const renderActiveComponent = () => {
    switch (active) {
      case 'Customer Information':
        return <OnboardingDetails onboardData={onboardingDetails} />;
      case 'Financial Activity':
        return <FinancialActivity data={financialData} />;
      case 'Behavioral & Device Information':
        return <BehavioralAndDevice data={behavioralData} />;
      case 'Network & Relationship Analysis':
        return <NetworkAndRelationship data={networkData} />;
      case 'Location & Geographic Activity':
        return <LocationAndGeographic data={locationData} />;
      default:
        return <OnboardingDetails onboardData={onboardingDetails} />;
    }
  };

  return (
    <DashboardLayout
      bg="bg-white80"
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Profiles</p>
      }
    >
      <div className="flex bg-white items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {label: 'Profiles', path: PAGES_URL.PROFILED_RISK_PROFILES},
            {label: slug},
          ]}
        />

        <div className="flex items-center gap-2">
          <SecondaryButton
            buttonText={`View Events (${quickSummary.totalEvents})`}
            onClick={() =>
              navigate(
                Constants.PAGES_URL.FLOWS_ACCOUNTS_EVENTS?.replace(
                  ':slug',
                  slug,
                )?.replace(':id', state?.profile_id || slug),
                {
                  state: {
                    profile_id: state?.profile_id || slug,
                  },
                },
              )
            }
          />
          <SecondaryButton
            buttonText={`View Cases (${quickSummary.totalCases})`}
            onClick={() =>
              navigate(
                Constants.PAGES_URL.FLOWS_ACCOUNTS_CASES?.replace(
                  ':slug',
                  slug,
                )?.replace(':id', state?.profile_id || slug),
                {
                  state: {
                    profile_id: state?.profile_id || slug,
                  },
                },
              )
            }
          />
          <PrimaryButton
            buttonText={isDownloading ? 'Downloading...' : 'Download Report'}
            disabled={isDownloading}
            onClick={handleDownload}
          />
        </div>
      </div>

      <div className="flex flex-col xl:flex-row w-full pt-6 gap-4 items-start">
        <div className="w-full xl:w-[67%] flex flex-col md:flex-row gap-4">
          <ProfileSidebar
            quickSummary={quickSummary}
            active={active}
            setActive={setActive}
            riskTier={quickSummary.riskTier}
            isDev={isDev}
          />
          {isLoading ? (
            <div className="w-full md:w-[70%]">
              <Loader height={45} />
            </div>
          ) : (
            <div className="md:w-[70%] w-full">{renderActiveComponent()}</div>
          )}
        </div>

        <div className="w-full xl:w-[33%] bg-white py-4 px-3 rounded-xl">
          <div
            style={{
              backgroundColor: lighterShade,
              border: '1px solid #F2F6FD',
            }}
            className="rounded-xl p-3 flex items-center justify-between"
          >
            <div className="flex flex-col gap-1">
              <h4 className="text-[#858DAA] text-small">Profile ID</h4>
              <p className="text-deepBlue text-tiny">{userEntity?.id}</p>
            </div>
            <SecondaryButton
              buttonText="Copy"
              onClick={() => handleCopy(userEntity?.id)}
            />
          </div>

          <div className="mt-4 border border-[#D9E5F8] rounded-xl bg-white relative">
            <div className="relative w-fit mt-8 mx-auto flex items-center justify-center">
              <p className="absolute bottom-6 left-[-0.5rem] uppercase   font-medium text-[#55A249] text-xs">
                LOW
              </p>
              <p className="absolute bottom-6 right-[-0.5rem] uppercase   font-medium text-[#B42318] text-xs">
                High
              </p>

              <ReactSpeedometer
                value={quickSummary.riskScoreValue}
                maxValue={100}
                needleHeightRatio={0.7}
                labelFontSize={'12px'}
                valueTextFontSize={'23px'}
                textColor="transparent"
                height={150}
                width={230}
                ringWidth={35}
                paddingHorizontal={10}
                forceRender={true}
                needleColor="#033971"
                segments={5}
                segmentColors={[
                  '#2E7D32',
                  '#9CCC65',
                  '#FFCA28',
                  '#FFA726',
                  '#B42318',
                ]}
              />
            </div>
            <div className="flex px-5 mt-4 items-start justify-between w-full absolute top-0">
              <div className="flex flex-col gap-1">
                <p className="text-[#858DAA] text-small">Risk level</p>
                <span
                  className="py-1 px-2 font-medium flex items-center justify-center text-[10px]  rounded-full"
                  style={{
                    color: getSeverityColor(quickSummary.riskTier),
                    backgroundColor: getSeverityColor(
                      quickSummary.riskTier,
                      'tint',
                    ),
                  }}
                >
                  {quickSummary.riskTier}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[#858DAA] text-small">Risk score</p>
                <h2 className="text-deepBlue font-semibold text-md">
                  {averageRiskScore}
                </h2>
              </div>
            </div>
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-2">
            <div
              style={{
                backgroundColor: lighterShade,
                border: '1px solid #F2F6FD',
              }}
              className="flex flex-col gap-1 rounded-xl py-[18px] px-3"
            >
              <h6 className="text-[#858DAA] text-small">Date created</h6>
              <p className="text-small font-medium text-deepBlue">
                {moment(userEntity?.createdAt).format('DD MMM, YYYY. hh:mm A')}
              </p>
            </div>
            <div
              style={{
                backgroundColor: lighterShade,
                border: '1px solid #F2F6FD',
              }}
              className="flex flex-col gap-1 rounded-xl py-[18px] px-3"
            >
              <h6 className="text-[#858DAA] text-small">Date updated</h6>
              <p className="text-small font-medium text-deepBlue">
                {moment(userEntity?.registration_time).format(
                  'DD MMM, YYYY. hh:mm A',
                )}
              </p>
            </div>
          </div>

          {isDev && <ActivityLog title="Recent activity" />}
          <CommentSection />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ProfileRiskDetails;
